<template>
  <div>
    <div v-if="pathway.steps.length > 1" class="flex flex-start flex-wrap gap-4 my-3">
      <div
        v-for="(step, index) in pathway.steps"
        :key="index"
        class="px-4 py-2 rounded-lg flex flex-row"
        :class="index + 1 === currentStep ? 'bg-pink-lighter' : index + 1 !== currentStep ? 'bg-white text-gray-400' : 'bg-white'"
      >
        <img
          v-if="isStepCompleted(index)"
          alt="Green check"
          class="w-6 mr-2"
          :src="greenCheck"
        >
        <div
          v-else
          class="text-2xl font-bold mr-2"
        >
          {{ index + 1 }}
        </div>
        <div style="margin-top: 5px">
          {{ step.title }}
        </div>
      </div>
    </div>

    <div class="rounded-lg">
      <v-stepper
        v-if="currentStep"
        v-model="currentStep"
        alt-labels
        class="!shadow rounded-lg"
      >
        <v-stepper-window
          :model-value="currentStep"
          style="margin: 0;"
        >
          <v-stepper-window-item
            v-for="(step, index) in pathway.steps"
            :key="index"
            :value="step.index"
            style="padding: 0;"
          >
            <component
              :is="stepComponent(step)"
              :pathway="pathway"
              :step="currentStep === step.index ? pathway.next_step : step"
              :consumer-key="consumerKey"
              :active="currentStep === step.index"
              :loading="loading"
              @start="() => $emit('start')"
              @finish="() => finish(step)"
              @skip-delay="$emit('skipDelay')"
            />
          </v-stepper-window-item>
        </v-stepper-window>
      </v-stepper>
      <PathwayEndFallbackStep v-else :pathway="pathway" :consumer-key="consumerKey" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import {computed, ComputedRef, ref} from "vue";
import pathwayService from '@/pathway/services/PathwayService';
import PathwayAftercareStep from '@/pathway/components/PathwayAftercareStep.vue';
import PathwayLoopStep from '@/pathway/components/PathwayLoopStep.vue';
import PathwayGuidanceStep from '@/pathway/components/PathwayGuidanceStep.vue';
import PathwayOnboardingStep from '@/pathway/components/PathwayOnboardingStep.vue';
import PathwayAssistedSaleStep from "@/pathway/components/PathwayAssistedSaleStep.vue";
import PathwaySurveyStep from '@/pathway/components/PathwaySurveyStep.vue';
import PathwayEnd from '@/pathway/components/PathwayEnd.vue';
import greenCheck from '@/assets/icons/green-check.svg';
import {PathwayStep, Pathway, SupportProgram} from "@/program/interfaces/Program";
import PathwayEndFallbackStep from "@/pathway/components/PathwayEndFallbackStep.vue";
import PathwayDelayStep from "@/pathway/components/PathwayDelayStep.vue";

const props = defineProps<{
    supportProgram: SupportProgram,
    consumerKey: string
}>();

defineEmits(['skipDelay', 'start']);
const loading = ref<boolean>(false);

const pathway: ComputedRef<Pathway> = computed(() => props.supportProgram.pathway);

const currentStep = computed((): number => {
    return pathway.value.next_step?.index ?? null;
});

/**
 * Check if the step is completed.
 * @param index
 */
const isStepCompleted = (index: number) => {
    return currentStep.value > index + 1;
};

/**
 * Finish the current step.
 * @param step  The current step object.
 */
const finish = (step: PathwayStep) => {
    loading.value = true;
    return pathwayService().finish(pathway.value, step, props.consumerKey)
        .finally(() => loading.value = false);
};

/**
 * Check if the current step is the end of the pathway.
 */
const isPathwayEnd = () => {
    const isRepeating = pathway.value.steps.some((step: PathwayStep) => !!step.repeating);
    const isLastStep = currentStep.value === pathway.value.steps.length;

    return isLastStep && !isRepeating;
};

/**
 * Get the component name for the step.
 * @param step  The pathway step object.
 */
const stepComponent = (step: PathwayStep) => {
    if (!isReady(step)) {
        return PathwayDelayStep;
    }

    switch (step.type) {
        case 'aftercare':
            return PathwayAftercareStep;
        case 'guidance':
            return PathwayGuidanceStep;
        case 'onboarding':
            return PathwayOnboardingStep;
        case 'assisted-sale':
            return PathwayAssistedSaleStep;
        case 'survey':
        case 'survey-bundle':
            return PathwaySurveyStep;
        default:
            return isPathwayEnd() ? PathwayEnd : PathwayLoopStep;
    }
};

/**
 * Check if the step is ready to be started.
 * @param step  The current step object.
 */
const isReady = (step: PathwayStep) => {
    if (!step.delay) {
        return true;
    }

    return step.elapsed >= step.delay;
};
</script>
